import {TimeUtil} from '@shared/util/time.util';
import {RecurringUnit} from '@domain/common/common';
import {ActivityScheduleDTO} from '@domain/activity/schedule/schedule.dto';
import {computed} from '@angular/core';
import {ActivityDTO} from '@domain/activity/activity.dto';

export class ActivityScheduleUtil {

  public static readonly MAX_SCHEDULES = 10;

  public static readonly nextSessionMinStartDate = TimeUtil.addDays(0);
  public static readonly nextSessionMaxStartDate = TimeUtil.addDays(180);
  public static readonly VALIDITY_MAX_DAYS = 450;
  public static readonly VALIDITY_MAX_DATE = TimeUtil.addDays(this.VALIDITY_MAX_DAYS);

  public static readonly EXPIRING_SOON_DAYS = 30;
  public static readonly EXPIRING_SOON_THRESHOLD = TimeUtil.addDays(this.EXPIRING_SOON_DAYS);

  public static sort(schedules: ActivityScheduleDTO[]): ActivityScheduleDTO[] {
    return schedules.sort((s1, s2) => ActivityScheduleUtil.compare(s1, s2));
  }

  private static compare(s1: ActivityScheduleDTO, s2: ActivityScheduleDTO): number {
    const isFinished1 = ActivityScheduleUtil.isFinished(s1);
    const isFinished2 = ActivityScheduleUtil.isFinished(s2);
    if (isFinished1 !== isFinished2) {
      return isFinished1 ? 1 : -1;
    }
    const startDay1 = s1.startDay ?? null;
    const startDay2 = s2.startDay ?? null;
    if (startDay1 !== null && startDay2 !== null) {
      if (startDay1 !== startDay2) {
        return startDay1 - startDay2;
      }
      const startTime1 = s1.startTime ?? null;
      const startTime2 = s2.startTime ?? null;
      if (startTime1 !== null && startTime2 !== null) {
        return startTime1 > startTime2 ? 1 : -1;
      }
    } else if (startDay1 === null || startDay2 === null) {
      return startDay1 === null ? 1 : -1;
    }
    const recurringUnit1 = s1.recurringUnit ?? null;
    const recurringUnit2 = s2.recurringUnit ?? null;
    if (recurringUnit1 !== recurringUnit2) {
      if (recurringUnit1 === RecurringUnit.OneOff || recurringUnit2 === RecurringUnit.OneOff) {
        return recurringUnit1 === RecurringUnit.OneOff ? 1 : -1;
      }

      if (recurringUnit1 === RecurringUnit.Week || recurringUnit2 === RecurringUnit.Week) {
        return recurringUnit1 === RecurringUnit.Week ? 1 : -1;
      }
    }
    return 0;
    // return this.getScheduleWeekTime(s1).getTime() - this.getScheduleWeekTime(s2).getTime();
  }

  // public static getNumberOfSessions(schedule: ActivitySchedule): number | null {
  //   const recurringUnit = schedule?.recurringUnit;
  //   if (!recurringUnit) {
  //     return 1;
  //   }
  //   if (
  //     recurringUnit === RecurringUnit.Week
  //     && schedule.nextStartTime
  //     && schedule.lastStartTime
  //   ) {
  //     const nextStart = new Date(schedule.nextStartTime);
  //     const lastStart = new Date(schedule.lastStartTime);
  //     return TimeUtil.weeksBetween(
  //       TimeUtil.addDays(-1, nextStart.getTime()),
  //       lastStart
  //     );
  //   }
  //   return null;
  // }

  public static getNextSchedule(schedules: ActivityScheduleDTO[]): ActivityScheduleDTO | null {
    let nextSchedule: ActivityScheduleDTO | null = null;
    let nextScheduleSession: Date | null = null;
    for (const schedule of schedules) {
      const nextSession = this.getNextSession(schedule);
      if (nextSession && (!nextScheduleSession || nextSession < nextScheduleSession)) {
        nextScheduleSession = nextSession;
        nextSchedule = schedule;
      }
    }
    return nextSchedule
      ? nextSchedule
      : (schedules.find(s => s.recurringUnit === null || s.recurringUnit === RecurringUnit.Sporadic) ?? null);
  }

  public static isTimeSet(schedule: ActivityScheduleDTO): boolean {
    return schedule?.recurringUnit !== null;
  }

  public static isAudienceSet(schedule: ActivityScheduleDTO): boolean {
    return schedule?.gender !== null
      && schedule?.level !== null
      && (schedule?.minAge !== null || schedule?.maxAge !== null);
  }

  public static canBeImproved(schedule: ActivityScheduleDTO): boolean {
    return !this.isFinished(schedule) && (
      schedule?.recurringUnit === null
      || schedule?.gender === null
      || schedule?.level === null
      || (schedule?.minAge === null && schedule?.maxAge === null)
    );
  }

  public static getActiveSchedules(schedules: ActivityScheduleDTO[]): ActivityScheduleDTO[] {
    return schedules.filter(s => !ActivityScheduleUtil.isFinished(s))
  }

  public static getImproveMessages(schedule: ActivityScheduleDTO): string[] {
    if (this.isFinished(schedule)) {
      return [];
    }
    const msgs: string[] = [];
    if (schedule?.recurringUnit === null) {
      msgs.push("L'horaire n'est pas indiqué.");
    }
    if (schedule?.gender === null) {
      msgs.push("Le genre cible n'est pas indiqué.");
    }
    if (schedule?.level === null) {
      msgs.push("Le niveau cible n'est pas indiqué.");
    }
    if (schedule?.minAge === null && schedule?.maxAge === null) {
      msgs.push("L'âge cible n'est pas indiqué.");
    }
    return msgs;
  }

  // public static isFinished(schedule: ActivitySchedule): boolean {
  //   const now = new Date();
  //   const inputDate = new Date(dateStr);
  //   const today = new Date();
  //
  //   // Set today to midnight to compare only the calendar date
  //   today.setHours(0, 0, 0, 0);
  //
  //   return inputDate < today;
  //   const lastDate = schedule.lastStartTime ?? schedule.nextEndTime ?? null;
  //   return !!lastDate && now > new Date(lastDate);
  // }

  // public static isActive(schedule: ActivitySchedule): boolean {
  //   const now = new Date()
  //   const lastDate = schedule.lastStartTime ?? schedule.nextEndTime ?? null;
  //   return !!lastDate && now <= new Date(lastDate);
  // }

  public static getNextSession(schedule: ActivityScheduleDTO): Date | null {
    const recurringUnit = schedule?.recurringUnit;
    if (!recurringUnit) {
      return null;
    }
    switch (recurringUnit) {
      case RecurringUnit.Sporadic:
        return null;
      case RecurringUnit.OneOff:
        return schedule.validFrom ? new Date(schedule.validFrom) : null;
      case RecurringUnit.Week:
        const startDay = schedule.startDay;
        const startTime = schedule.startTime;
        if (!(startDay && startTime)) {
          return null;
        }
        const now = new Date();
        const validityStart = TimeUtil.toDate(schedule.validFrom, startTime) ?? now;

        if (validityStart > now) {
          return TimeUtil.getNextWeekDay(validityStart, startTime, startDay);
        } else {
          const validityEnd = TimeUtil.toDate(schedule.validUntil, startTime);
          if (!validityEnd) {
            return null;
          }

          if (validityEnd < now) {
            return null;
          }
          const today = TimeUtil.toDate(TimeUtil.toIsoDate(now), startTime);
          return TimeUtil.getNextWeekDay(today, startTime, startDay);
        }
      default:
        throw new Error('Unsupported recurring unit: ' + recurringUnit);
    }
  }

  public static isRecurring(schedule: ActivityScheduleDTO): boolean {
    return !!schedule?.recurringUnit && [RecurringUnit.Sporadic, RecurringUnit.Week].includes(schedule.recurringUnit);
  }

  public static isNotYetValid(schedule: ActivityScheduleDTO): boolean {
    if (!schedule?.validFrom || !this.isRecurring(schedule)) {
      return false;
    }
    const validFrom = TimeUtil.fromIsoDateTime(schedule.validFrom);
    return !!validFrom && validFrom.getTime() > (new Date()).getTime();
  }

  public static isExpiringSoon(schedule: ActivityScheduleDTO): boolean {
    if (!schedule?.validUntil || !this.isRecurring(schedule)) {
      return false;
    }
    const validUntil = TimeUtil.fromIsoDateTime(schedule.validUntil);
    return !!validUntil && validUntil.getTime() < ActivityScheduleUtil.EXPIRING_SOON_THRESHOLD.getTime();
  }

  public static isFinished(schedule: ActivityScheduleDTO): boolean {
    if (!schedule.validUntil) {
      return false;
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const validUntil = new Date(schedule.validUntil);
    validUntil.setHours(0, 0, 0, 0);
    return validUntil < today;
  }

  // public static getNextSession(schedule: ActivitySchedule): Date | null {
  //   const recurringUnit = schedule?.recurringUnit;
  //   if (!schedule.nextStartTime) {
  //     return null;
  //   }
  //   const nextStart = new Date(schedule.nextStartTime);
  //   const now = new Date();
  //   if (!recurringUnit) {
  //     return nextStart >= now ? nextStart : null;
  //   }
  //   if (recurringUnit === RecurringUnit.Week) {
  //     if (!schedule.lastStartTime) {
  //       return null;
  //     }
  //     const lastStart = new Date(schedule.lastStartTime);
  //     if (lastStart < now) {
  //       return null;
  //     }
  //     const nbWeeksUntilEnd = TimeUtil.weeksBetween(now, lastStart, false);
  //     if (nbWeeksUntilEnd !== null && nbWeeksUntilEnd >= 0) {
  //       return TimeUtil.nextSwissWeek(lastStart, -nbWeeksUntilEnd);
  //     }
  //   }
  //   return null
  // }

  // private static getScheduleWeekTime(schedule: ActivitySchedule): Date {
  //   const recurringUnit = schedule?.recurringUnit;
  //   const startDay = schedule.startDay;
  //   const startTime = schedule.startTime;
  //   const validFrom = schedule.validFrom;
  //
  //   switch (recurringUnit) {
  //     case RecurringUnit.Sporadic:
  //       return new Date();
  //     case RecurringUnit.Week:
  //
  //   }
  //   if (!schedule.nextStartTime) {
  //     return new Date();
  //   }
  //   const startDate = new Date(schedule.nextStartTime)
  //   if (!schedule.recurringUnit) {
  //     return startDate;
  //   }
  //   const targetDay = startDate.getDay();
  //   const now = new Date();
  //   const currentDay = now.getDay();
  //   const diff = targetDay - currentDay;
  //   const result = now;
  //   result.setDate(now.getDate() + diff);
  //   result.setHours(startDate.getHours(), startDate.getMinutes(), 0, 0);
  //   return result;
  // }

}
